import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, refreshSession, setAccessToken } from "./api";

export interface Membership { tenantId: string; name: string }
export interface Me { user: { id: string; fullName: string; email: string | null }; tenantId: string | null; mfa: boolean; memberships: Membership[]; platformStaff?: boolean }

interface SessionCtx {
  me: Me | null; ready: boolean;
  reload: () => Promise<Me | null>;
  signedIn: (accessToken: string, memberships: Membership[]) => Promise<"app" | "choose" | "none" | "platform">;
  chooseTenant: (tenantId: string) => Promise<void>;
  signOut: () => Promise<void>;
}
const Ctx = createContext<SessionCtx>(null as unknown as SessionCtx);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [ready, setReady] = useState(false);

  const reload = useCallback(async () => {
    try { const m: Me = await api.authed("/api/v1/auth/me"); setMe(m); return m; } catch { setMe(null); return null; }
  }, []);

  // Restore a session after a page reload from the HttpOnly refresh cookie.
  // The waiting-room TV has no staff session; don't try to restore one there.
  useEffect(() => { if (location.pathname.startsWith("/display/")) { setReady(true); return; } (async () => { if (await refreshSession()) await reload(); setReady(true); })(); }, [reload]);

  const chooseTenant = useCallback(async (tenantId: string) => {
    const r = await api.authed("/api/v1/auth/select-tenant", { body: { tenantId } });
    setAccessToken(r.accessToken);
    await reload();
  }, [reload]);

  const signedIn = useCallback(async (token: string, memberships: Membership[]) => {
    setAccessToken(token);
    if (memberships.length === 1) { await chooseTenant(memberships[0]!.tenantId); return "app"; }
    const m = await reload();
    // Platform staff usually have no hospital membership: send them to the console instead of a dead end.
    if (memberships.length === 0) return m?.platformStaff ? "platform" : "none";
    return "choose";
  }, [chooseTenant, reload]);

  const signOut = useCallback(async () => { await api.logout().catch(() => undefined); setMe(null); }, []);

  return <Ctx.Provider value={{ me, ready, reload, signedIn, chooseTenant, signOut }}>{children}</Ctx.Provider>;
}
export const useSession = () => useContext(Ctx);
